import { useCallback } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { Icon } from 'components/Icon';
import { RouterLink } from 'components/Link';
import { NotificationContent } from 'components/Notifications/NotificationContent';
import { colors, fontWeights } from 'theme';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { MappedLocalNotification } from 'utils/localNotifications';

import { CloseButton } from './UserNotification.styled';

const Tag = styled(Box)`
  width: 4px;
  height: 4px;
  padding: 4px;
  border-radius: 50%;
  background-color: ${colors.brandYellow};
`;

const StyledLink = styled(RouterLink)`
  display: flex;
  padding: 12px;
  padding-right: 30px;
  color: ${colors.mediumContentGrey};
  font-weight: ${fontWeights.normal};
  text-decoration: none;
  border-bottom: 1px solid ${colors.selectionGrey};

  &:hover {
    color: ${colors.mediumContentGrey};
    text-decoration: none;
    background-color: ${colors.inputGrey};
  }

  &:active {
    background-color: ${colors.softBlue};
  }
`;

type Props = {
  notification: MappedNotification | MappedLocalNotification;
  onClick?: (
    notification: MappedNotification | MappedLocalNotification,
  ) => void;
  onDismiss?: (
    notification: MappedNotification | MappedLocalNotification,
  ) => void;
  qaId?: string;
};

export function BaseNotification({
  notification,
  qaId,
  onClick,
  onDismiss,
}: Props) {
  const handleClick = useCallback(
    (eventName: string, eventProperties: Record<string, unknown>) => {
      trackClicked(eventName, eventProperties);
      if (onClick) onClick(notification);
    },
    [onClick, notification],
  );

  return (
    <Box
      data-qa-id={qaId}
      data-qa-read={
        // eslint-disable-next-line no-nested-ternary
        'read' in notification
          ? notification.read
            ? 'true'
            : 'false'
          : undefined
      }
      position="relative"
      minWidth="260px"
    >
      <NotificationContent
        notification={notification}
        render={({ to, eventName, eventProperties, children }) => (
          <StyledLink
            to={to}
            onClick={() => handleClick(eventName, eventProperties)}
          >
            <Box pt="6px" width="20px" flex="0 0 auto">
              {'read' in notification && !notification.read ? <Tag /> : null}
            </Box>

            <Box>{children}</Box>

            {onDismiss ? (
              <Box position="absolute" top="8px" right="8px">
                <CloseButton
                  data-qa-id="dismiss-notification"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDismiss(notification);
                  }}
                >
                  <Icon
                    initialColorName="lightContentGrey"
                    name="close"
                    size={12}
                  />
                </CloseButton>
              </Box>
            ) : null}
          </StyledLink>
        )}
      />
    </Box>
  );
}
